import axios from "axios";
import React, { useEffect, useMemo, useState } from "react";
import { FaPaperPlane, FaRobot, FaSpinner, FaUser } from "react-icons/fa";
import { rootUrl } from "../../../helpers";
import { useAuth } from "../../../context/auth-context";
import { useOutletContext } from 'react-router-dom';

function looksLikeMarkdownTable(lines, i) {
  if (i + 1 >= lines.length) return false;
  return lines[i].includes("|") && /^\s*\|?[\s:-]+\|[\s|:-]*$/.test(lines[i + 1]);
}

function hasMarkdownTable(content) {
  const lines = String(content || "").split("\n");
  for (let i = 0; i < lines.length - 1; i += 1) {
    if (looksLikeMarkdownTable(lines, i)) return true;
  }
  return false;
}

function renderAssistantContent(content) {
  const lines = String(content || "").split("\n");
  const blocks = [];
  let i = 0;

  while (i < lines.length) {
    if (looksLikeMarkdownTable(lines, i)) {
      const header = lines[i].split("|").map((c) => c.trim()).filter(Boolean);
      i += 2;
      const rows = [];
      while (i < lines.length && lines[i].includes("|")) {
        const cells = lines[i].split("|").map((c) => c.trim()).filter(Boolean);
        if (cells.length) rows.push(cells);
        i += 1;
      }
      blocks.push(
        <div key={`table-${i}`} className="overflow-auto border border-gray-200 rounded-lg my-2">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100">
              <tr>
                {header.map((h, idx) => (
                  <th key={idx} className="text-left px-3 py-2 font-semibold text-gray-700">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, ridx) => (
                <tr key={ridx} className="border-t border-gray-100">
                  {r.map((c, cidx) => (
                    <td key={cidx} className="px-3 py-2 text-gray-800">{c}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      continue;
    }

    const paragraph = [];
    while (i < lines.length && !looksLikeMarkdownTable(lines, i)) {
      paragraph.push(lines[i]);
      i += 1;
    }
    const text = paragraph.join("\n").trim();
    if (text) {
      blocks.push(
        <pre key={`p-${i}`} className="whitespace-pre-wrap font-sans text-gray-800">
          {text}
        </pre>
      );
    }
  }
  return blocks;
}

export default function AiAssistantReport() {
  const auth = useAuth();
  const {allData} = useOutletContext();
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [banks, setBanks] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [selectedBank, setSelectedBank] = useState(null);
  const [selectedPaye, setSelectedPaye] = useState(null);
  const [lastFilters, setLastFilters] = useState(null);
  const [lastLogId, setLastLogId] = useState(null);
  const [showExportOptions, setShowExportOptions] = useState(false);
  const [showFeedbackOptions, setShowFeedbackOptions] = useState(false);
  const [feedbackText, setFeedbackText] = useState("");
  const [emailInput, setEmailInput] = useState("");
  const [exportLoading, setExportLoading] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "Hi, I can generate cashbook reports from your message. You can type naturally like 'yesterday' or 'month of March 2025'. Click bank/paye suggestions so IDs are sent to backend for faster filtering.",
    },
  ]);

  useEffect(() => {
    const fetchLookupData = async () => {
      if (!auth.user?.cid) return;
      try {
        setBanks(allData?.bank || []);
        setSuppliers(allData?.suppliers || []);
      } catch (e) {
        setBanks([]);
        setSuppliers([]);
      }
    };
    fetchLookupData();
  }, [auth.user?.cid, auth.user?.id]);

  const canRun = useMemo(() => {
    return prompt.trim().length > 0;
  }, [prompt]);

  const bankSuggestions = useMemo(() => {
    const text = prompt.toLowerCase();
    if (!text.includes("bank")) return [];
    const match = prompt.match(/bank\s+([a-zA-Z0-9\s-]*)$/i);
    if (!match) return [];
    const term = match ? match[1].trim().toLowerCase() : "";
    const list = banks
      .filter((b) => {
        const name = String(b?.name || "").toLowerCase();
        return term ? name.includes(term) : true;
      })
      .slice(0, 8);
    return list;
  }, [prompt, banks]);

  const applyBankSuggestion = (bankName) => {
    const selected = banks.find((b) => String(b.name) === String(bankName));
    setSelectedBank(selected ? { id: selected.id, name: selected.name } : null);
    setPrompt((prev) => {
      if (/bank\s+[a-zA-Z0-9\s-]*$/i.test(prev)) {
        return prev.replace(/bank\s+[a-zA-Z0-9\s-]*$/i, `bank ${bankName}`);
      }
      return `${prev}${prev.endsWith(" ") || prev.length === 0 ? "" : " "}bank ${bankName}`;
    });
  };

  const payeSuggestions = useMemo(() => {
    const text = prompt.toLowerCase();
    if (!text.includes("paye")) return [];
    const match = prompt.match(/paye\s+([a-zA-Z0-9\s-]*)$/i);
    if (!match) return [];
    const term = match ? match[1].trim().toLowerCase() : "";
    
    return suppliers
      .filter((s) => {
        const name = String(s?.name || "").toLowerCase();
        return term ? name.includes(term) : true;
      })
      .slice(0, 8);
  }, [prompt, suppliers]);

  const applyPayeSuggestion = (payeName) => {
    const selected = suppliers.find((s) => String(s.name) === String(payeName));
    setSelectedPaye(selected ? { id: selected.id, name: selected.name } : null);
    setPrompt((prev) => {
      if (/paye\s+[a-zA-Z0-9\s-]*$/i.test(prev)) {
        return prev.replace(/paye\s+[a-zA-Z0-9\s-]*$/i, `paye ${payeName}`);
      }
      return `${prev}${prev.endsWith(" ") || prev.length === 0 ? "" : " "}paye ${payeName}`;
    });
  };

  const handleSend = async () => {
    if (!canRun) return;
    const userMessage = prompt.trim();
    const nextMessages = [...messages, { role: "user", content: userMessage }];
    setMessages(nextMessages);
    setPrompt("");
    setLoading(true);
    try {
      const promptForBackend = `${userMessage}${
        selectedBank?.id ? `\n[bank_id:${selectedBank.id}]` : ""
      }${selectedPaye?.id ? `\n[paye_id:${selectedPaye.id}]` : ""}`;

      const payload = {
        cid: auth.user?.cid,
        uid: auth.user?.id,
        prompt: promptForBackend,
        selectedBankId: selectedBank?.id || null,
        selectedPayeId: selectedPaye?.id || null,
        history: nextMessages.slice(-6),
      };
      const res = await axios.post(rootUrl("/aiCashbookReport"), payload);
      const reply = res?.data?.data?.reply || "I could not generate a response.";
      const rowsAnalyzed = res?.data?.data?.rowsAnalyzed;
      const filters = res?.data?.data?.filters || null;
      const logId = res?.data?.data?.logId || null;
      const replyHasTable = hasMarkdownTable(reply);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: `${reply}${typeof rowsAnalyzed === "number" ? `\n\n(Rows analyzed: ${rowsAnalyzed})` : ""}`,
        },
      ]);
      setLastFilters(filters);
      setLastLogId(logId);
      setShowExportOptions(replyHasTable);
      setShowFeedbackOptions(true);
      setSelectedBank(null);
      setSelectedPaye(null);
    } catch (e) {
      const apiMessage = e?.response?.data?.message || e?.response?.data?.data?.error;
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: apiMessage || "Failed to generate report. Please try again." },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadExcel = async () => {
    if (!lastFilters) return;
    setExportLoading(true);
    try {
      const res = await axios.post(rootUrl("/aiCashbookReportExport"), {
        cid: auth.user?.cid,
        logId: lastLogId,
        filters: lastFilters,
        mode: "download",
      });
      const payload = res?.data?.data;
      const base64 = payload?.base64;
      const filename = payload?.filename || "ai_cashbook_report.xls";
      if (!base64) throw new Error("Missing export file");
      const binary = atob(base64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
      const blob = new Blob([bytes], { type: payload?.mime || "application/vnd.ms-excel" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setMessages((prev) => [...prev, { role: "assistant", content: "Excel report downloaded in browser." }]);
      setShowExportOptions(false);
    } catch (e) {
      setMessages((prev) => [...prev, { role: "assistant", content: "Failed to download excel report." }]);
    } finally {
      setExportLoading(false);
    }
  };

  const handleEmailExcel = async () => {
    if (!lastFilters || !emailInput.trim()) return;
    setExportLoading(true);
    try {
      const res = await axios.post(rootUrl("/aiCashbookReportExport"), {
        cid: auth.user?.cid,
        logId: lastLogId,
        filters: lastFilters,
        mode: "email",
        email: emailInput.trim(),
      });
      const msg = res?.data?.message || "Excel report sent to email.";
      setMessages((prev) => [...prev, { role: "assistant", content: msg }]);
      setShowExportOptions(false);
      setEmailInput("");
    } catch (e) {
      const msg = e?.response?.data?.message || "Failed to email excel report.";
      setMessages((prev) => [...prev, { role: "assistant", content: msg }]);
    } finally {
      setExportLoading(false);
    }
  };

  const submitFeedback = async (satisfied) => {
    if (!lastLogId) return;
    try {
      await axios.post(rootUrl("/aiCashbookFeedback"), {
        logId: lastLogId,
        satisfied: satisfied ? 1 : 0,
        comment: feedbackText.trim(),
      });
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: "Thanks, your feedback has been saved." },
      ]);
      setShowFeedbackOptions(false);
      setFeedbackText("");
    } catch (e) {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: "Could not save feedback right now." },
      ]);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 bg-gradient-to-r from-blue-950 to-blue-800 text-white flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-white/20 p-2 rounded-lg">
              <FaRobot className="text-xl" />
            </div>
            <div>
              <h2 className="text-xl font-bold">AI Report Assistant</h2>
              <p className="text-sm text-blue-100">
                Chat naturally. Mention date range and any details in your prompt.
              </p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-4">
          <div className="min-h-[340px] max-h-[560px] overflow-auto rounded-xl border border-gray-200 bg-gray-50 p-4 space-y-4">
            {messages.map((msg, idx) => (
              <div key={idx} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] rounded-xl px-4 py-3 text-sm shadow-sm ${
                    msg.role === "user" ? "bg-blue-950 text-white" : "bg-white text-gray-800 border border-gray-200"
                  }`}
                >
                  <div className="font-semibold text-xs mb-1 flex items-center gap-2">
                    {msg.role === "user" ? <FaUser size={12} /> : <FaRobot size={12} />}
                    {msg.role === "user" ? "You" : "Assistant"}
                  </div>
                  {msg.role === "assistant" ? (
                    <div className="space-y-2">{renderAssistantContent(msg.content)}</div>
                  ) : (
                    <pre className="whitespace-pre-wrap font-sans text-white">{msg.content}</pre>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="rounded-xl px-4 py-3 text-sm shadow-sm bg-white text-gray-800 border border-gray-200 flex items-center gap-2">
                  <FaSpinner className="animate-spin" />
                  Thinking...
                </div>
              </div>
            )}
          </div>

          <div className="flex items-end gap-3">
            <div className="w-full space-y-2">
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                rows={3}
                placeholder="Ask for a report. Include date range and filters in your message."
                className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              {bankSuggestions.length > 0 && (
                <div className="rounded-lg border border-gray-200 bg-white p-2 flex flex-wrap gap-2">
                  <span className="text-xs text-gray-500 mr-1">Bank suggestions:</span>
                  {bankSuggestions.map((b) => (
                    <button
                      type="button"
                      key={b.id}
                      onClick={() => applyBankSuggestion(b.name)}
                      className="text-xs px-2 py-1 rounded-md bg-gray-100 hover:bg-blue-100 text-gray-700"
                    >
                      {b.name}
                    </button>
                  ))}
                </div>
              )}
              {selectedBank && (
                <div className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-md px-2 py-1 inline-block">
                  Selected bank: {selectedBank.name}
                </div>
              )}
              {payeSuggestions.length > 0 && (
                <div className="rounded-lg border border-gray-200 bg-white p-2 flex flex-wrap gap-2">
                  <span className="text-xs text-gray-500 mr-1">Paye suggestions:</span>
                  {payeSuggestions.map((s) => (
                    <button
                      type="button"
                      key={s.id}
                      onClick={() => applyPayeSuggestion(s.name)}
                      className="text-xs px-2 py-1 rounded-md bg-gray-100 hover:bg-blue-100 text-gray-700"
                    >
                      {s.name}
                    </button>
                  ))}
                </div>
              )}
              {selectedPaye && (
                <div className="text-xs text-green-700 bg-green-50 border border-green-200 rounded-md px-2 py-1 inline-block">
                  Selected paye: {selectedPaye.name}
                </div>
              )}
              {showExportOptions && (
                <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 space-y-2">
                  <p className="text-sm text-blue-900 font-medium">Would you like this report in Excel?</p>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={handleDownloadExcel}
                      disabled={exportLoading}
                      className="text-xs px-3 py-1.5 rounded-md bg-blue-900 text-white hover:bg-blue-800 disabled:opacity-60"
                    >
                      {exportLoading ? "Processing..." : "Download Here"}
                    </button>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="email"
                      value={emailInput}
                      onChange={(e) => setEmailInput(e.target.value)}
                      placeholder="Enter email to send excel"
                      className="border border-blue-200 rounded-md px-2 py-1 text-xs w-full bg-white"
                    />
                    <button
                      type="button"
                      onClick={handleEmailExcel}
                      disabled={exportLoading || !emailInput.trim()}
                      className="text-xs px-3 py-1.5 rounded-md bg-green-700 text-white hover:bg-green-600 disabled:opacity-60"
                    >
                      Send Email
                    </button>
                  </div>
                </div>
              )}
              {showFeedbackOptions && (
                <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm space-y-3">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <p className="text-sm font-semibold text-slate-800">Was this response helpful?</p>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => submitFeedback(true)}
                        className="text-xs px-3 py-1.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-500 transition-colors"
                      >
                        Yes
                      </button>
                      <button
                        type="button"
                        onClick={() => submitFeedback(false)}
                        className="text-xs px-3 py-1.5 rounded-lg bg-rose-600 text-white hover:bg-rose-500 transition-colors"
                      >
                        No
                      </button>
                    </div>
                  </div>
                  <input
                    type="text"
                    value={feedbackText}
                    onChange={(e) => setFeedbackText(e.target.value)}
                    placeholder="Optional feedback to improve future responses..."
                    className="border border-slate-200 rounded-lg px-3 py-2 text-xs w-full bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              )}
            </div>
            <button
              onClick={handleSend}
              disabled={!canRun || loading}
              className="bg-blue-950 text-white px-4 py-3 rounded-lg hover:bg-blue-900 disabled:opacity-60 disabled:cursor-not-allowed transition-colors font-medium text-sm flex items-center gap-2"
            >
              {loading ? <FaSpinner className="animate-spin" /> : <FaPaperPlane />}
              Send
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
